import { makeStyles, withStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import MuiTableHead from '@material-ui/core/TableHead';

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.custom.pageBackground,
    // Makes native scrollbars / form controls follow the active theme.
    colorScheme: theme.palette.type,
    '& .MuiPaper-root': {
      elevation: 3,
    },
  },
  projectName: {
    paddingLeft: '16px',
  },
  header: {
    display: 'flex',
    paddingBottom: '20px',
  },
  title: {
    flexGrow: 1,
    letterSpacing: '-0.5px',
  },
  taskName: {
    paddingLeft: '1%',
    marginLeft: '10px',
  },
  tableHeader: {
    textAlign: 'center',
  },
  surveyImageHeader: {
    textAlign: 'left',
  },
  logoUrl: {
    height: '45px',
  },
  questionsTitle: {
    // '#22617B' is exactly `primary.mainBlue`, which already has a dark value.
    color: theme.palette.primary.mainBlue,
    paddingLeft: '1%',
    fontWeight: 'bold',
    marginLeft: '10px',
  },
  progress: {
    height: '610px',
    display: 'table-cell',
    textAlign: 'center',
    padding: '50px',
    '& div': {
      color: theme.palette.primary.main,
    },
    '& h4': {
      color: theme.palette.primary.main,
      fontWeight: '200',
    },
  },
  taskContent: {
    display: 'flex',
    marginTop: '15px',
  },
  categoryContainer: {
    marginTop: '30px',
    borderTop: `1px solid ${theme.palette.primary.lightGray}`,
  },
  categoriesTitleContainer: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  categoryTitle: {
    textAlign: 'left',
    color: theme.palette.secondary.blue,
  },
  formDetail: {
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: '10px',
    backgroundColor: theme.palette.custom.cardBackground,
    boxShadow: '5px 5px 10px rgba(0, 0, 0, 0.02)',
    marginBottom: '15px',
    padding: '15px',
    color: theme.palette.primary.gray,
    '& .MuiIconButton-root': {
      padding: '0px',
      color: theme.palette.primary.lightGray,
    },
  },
  root: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    minHeight: 200,
  },
  table: {
    minWidth: 650,
  },
  categoryListTable: {
    '& .MuiTableCell-root': {
      color: theme.palette.primary.gray,
    },
    '& .MuiTableCell-head': {
      color: theme.palette.primary.gray,
    },
    '& .MuiTableCell-paddingNone': {
      color: theme.palette.primary.mainBlue,
      fontWeight: 'bold',
    },
    '& .Mui-checked': {
      color: theme.palette.secondary.blue,
    },
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: theme.palette.custom.tableRowHover,
      },
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.custom.tableRowHover,
    },
  },
  tag: {
    borderRadius: '5px',
    padding: '5px 8px',
    maxWidth: '160px',
    margin: '0px auto',
  },
  // Same light values the `secondary.*` pairs had; those keys are inherited
  // verbatim in dark mode, which left pastel fills glowing on a dark surface.
  tagCompleted: {
    color: theme.palette.custom.statusOpenText,
    backgroundColor: theme.palette.custom.statusOpenBackground,
  },
  tagNotStarted: {
    color: theme.palette.custom.statusRejectedText,
    backgroundColor: theme.palette.custom.statusRejectedBackground,
  },
  tagObjected: {
    color: theme.palette.custom.statusPendingText,
    backgroundColor: theme.palette.custom.statusPendingBackground,
  },
  tagPending: {
    color: theme.palette.custom.statusClosedText,
    backgroundColor: theme.palette.custom.statusClosedBackground,
  },
  tagIncomplete: {
    color: theme.palette.custom.statusPendingText,
    backgroundColor: theme.palette.custom.statusPendingBackground,
  },
  tagNA: {
    color: theme.palette.custom.statusIdleText,
    backgroundColor: theme.palette.custom.statusIdleBackground,
  },
  tagReaudit: {
    color: theme.palette.custom.statusApprovedText,
    backgroundColor: theme.palette.custom.statusApprovedBackground,
  },
  categoryRowTitles: {
    alignItems: 'center',
  },
  progressBarRoot: {
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarInfo,
  },
  toolbarTable: {
    paddingLeft: '0px',
    justifyContent: 'space-between',
  },
  // Row highlights for question / survey sections. '#ABEBC6' and '#D6EAF8' are
  // exactly the light values of these two status backgrounds.
  questionTable: {
    backgroundColor: theme.palette.custom.statusOpenBackground,
  },
  surveyTable: {
    backgroundColor: theme.palette.custom.statusClosedBackground,
  },
  enableButton: {
    backgroundColor: theme.palette.custom.solidButtonBackground,
    height: '40px',
    width: '180px',
    color: theme.palette.custom.solidButtonText,
    '&:hover': {
      backgroundColor: theme.palette.custom.solidButtonHoverSurface,
      color: theme.palette.custom.accentText,
    },
  },
  disableButton: {
    backgroundColor: theme.palette.custom.disabledButtonBackground,
    height: '40px',
    width: '180px',
    color: theme.palette.custom.solidButtonText,
  },
  fab: {
    position: 'absolute',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

export const RedBorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarDanger,
  },
}))(LinearProgress);

export const YellowBorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarWarning,
  },
}))(LinearProgress);

export const GreenBorderLinearProgress = withStyles((theme) => ({
  root: {
    height: 10,
    borderRadius: 5,
  },
  colorPrimary: {
    backgroundColor: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
  },
  bar: {
    borderRadius: 5,
    backgroundColor: theme.palette.custom.progressBarSuccess,
  },
}))(LinearProgress);

export const TableHead = withStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.secondary.lightBluegray,
  },
}))(MuiTableHead);

export default useStyles;
